// The countdown on the question screen. Display-only: all the timing lives in
// `useBmdTimer`, this just draws what the hook reports. No timing behaviour
// changed with the redesign.
//
// Redrawn as a ring to the PM's mockup. A ring reads as "time remaining" at a
// glance in a way MM:SS does not, which matters when the whole window is 60
// seconds and the reader is a child. The number inside stays in seconds for the
// same reason: "00:18" is a clock, "18" is a countdown.
//
// Phases:
//   MAIN     navy ring, draining clockwise from twelve o'clock
//   BUFFER   amber, the grace period after the advertised time
//   EXPIRED  a full red ring and "Time's up"
// A paused timer keeps its ring and gains a pause glyph. That only happens when
// the follow-ups have a countdown of their own; with one shared window the
// clock never pauses.

import React from "react"
import type { BmdTimerPhase } from "../../hooks/diagnostics/useBmdTimer"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import {
  BMD_MCQ_BUFFER_SECONDS,
  BMD_MCQ_TIMER_SECONDS,
} from "../../constants/diagnostics/bmd.constants"

export type BmdTimerProps = {
  remainingSeconds: number
  phase: BmdTimerPhase
  isPaused: boolean
  /** The window this countdown started from, so the ring knows what full is. */
  totalSeconds?: number
  className?: string
}

const SIZE = 46
const STROKE = 4
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export const BmdTimer: React.FC<BmdTimerProps> = ({
  remainingSeconds,
  phase,
  isPaused,
  totalSeconds = BMD_MCQ_TIMER_SECONDS,
  className,
}) => {
  const isBuffer = phase === "BUFFER"
  const isExpired = phase === "EXPIRED"

  const color = isExpired
    ? BMD_UI.RING_DANGER
    : isBuffer
    ? BMD_UI.RING_WARN
    : BMD_UI.RING_OK

  // The buffer counts down from its own, shorter window, so measuring it
  // against the 60-second total would draw a ring that starts nearly empty and
  // barely moves.
  const denominator = isBuffer
    ? Math.max(1, BMD_MCQ_BUFFER_SECONDS)
    : Math.max(1, totalSeconds)
  const fraction = isExpired
    ? 1
    : Math.min(1, Math.max(0, 1 - remainingSeconds / denominator))
  const dashOffset = CIRCUMFERENCE * fraction

  const seconds = Math.max(0, Math.ceil(remainingSeconds))

  return (
    <div
      className={`d-flex align-items-center gap-2 ${className ?? ""}`}
      role="timer"
      aria-live="off"
    >
      <div style={{ position: "relative", width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} aria-hidden="true">
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={BMD_UI.RING_TRACK}
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            style={{ transition: "stroke-dashoffset 900ms linear" }}
          />
        </svg>
        <div
          className="d-flex align-items-center justify-content-center fw-bold"
          style={{
            position: "absolute",
            inset: 0,
            color: isExpired ? BMD_UI.RING_DANGER : BMD_UI.TITLE,
            fontSize: 14,
          }}
        >
          {isPaused && !isExpired ? (
            <i className="fas fa-pause" style={{ fontSize: 12 }}></i>
          ) : isExpired ? (
            <i className="fas fa-hourglass-end" style={{ fontSize: 12 }}></i>
          ) : (
            seconds
          )}
        </div>
      </div>

      <span
        style={{
          color: isExpired ? BMD_UI.RING_DANGER : BMD_UI.BODY,
          fontSize: 12,
          lineHeight: 1.2,
          maxWidth: 54,
        }}
      >
        {isExpired ? (
          "Time's up"
        ) : isPaused ? (
          "paused"
        ) : (
          <>
            seconds
            <br />
            left
          </>
        )}
      </span>
    </div>
  )
}

export default BmdTimer
